<template>
  <div class="el-input" tabindex="0" @focus="focus" @blur="blur">
    <div
      class="el-input__wrapper"
      :class="{
        'el-input--center': alignCenter,
        'el-input--right': alignRight,
        'is-focus': isFocus,
      }"
    >
      <div
        v-if="$slots.prefix"
        class="el-input__prefix"
        @mousedown="elementDown"
      >
        <span class="el-input__prefix-inner">
          <slot name="prefix"></slot>
        </span>
      </div>
      <input
        ref="element"
        class="el-input__inner"
        :style="style"
        :value="displayValue"
        :placeholder="placeholder"
        @focus="focus"
        @blur="blur"
        @input="input"
        @change="change"
        @keydown.stop.up="increase"
        @keydown.stop.down="decrease"
      />
      <div v-if="suffix" class="el-input__suffix">
        <span class="el-input__suffix-inner">
          <span>{{ suffix }}</span>
        </span>
      </div>
    </div>
  </div>
</template>
<script setup>
const emit = defineEmits(["update:modelValue", "change", "input", "blur"]);
const props = defineProps({
  alignCenter: {
    type: Boolean,
    default: false,
  },
  alignRight: {
    type: Boolean,
    default: false,
  },
  rounded: {
    type: Boolean,
    default: false,
  },
  max: {
    type: Number,
  },
  min: {
    type: Number,
  },
  formatter: {
    type: String,
  },
  placeholder: {
    type: String,
  },
  step: {
    type: Number,
    default: 1,
  },
  modelValue: {
    type: Number,
    default: 0,
  },
});
const element = ref(null);
const isFocus = ref(false);
const dragStart = ref(false);
const dragging = ref(false);
const oldValue = ref(null);
const newValue = ref(null);
const displayValue = ref("");
const beforePosition = reactive({ value: 0, pageX: 0, pageY: 0 });
const style = computed(() => {
  let length = 0;

  for (const s of displayValue.value) {
    length += s === "." ? 0.5 : 1;
  }
  return {
    width: `${length}ch`,
  };
});
const suffix = computed(() => {
  switch (props.formatter) {
    case "percent":
      return "%";
    case "angle":
      return "°";
    case "speed":
      return "x";
    case "second":
    case "sec":
      return "s";
    default:
      return "";
  }
});

onMounted(() => {
  document.addEventListener("mousemove", handleMouseMove);
  window.addEventListener("mouseup", handleMouseUp);
});

watch(
  () => props.modelValue,
  (value) => {
    if (value === newValue.value) {
      return;
    }
    setValue(value);
  },
  { immediate: true }
);

function handleMouseMove(e) {
  if (dragStart.value) {
    drag(e);
  }
}

function handleMouseUp() {
  document.documentElement.style.removeProperty("cursor");
  document.body.style.removeProperty("pointer-events");

  resetPosition();
  dragStart.value = false;

  if (dragging.value) {
    dragging.value = false;
    emit("change", newValue.value);
  }
}

function elementDown(e) {
  dragStart.value = true;
  savePosition(e);
}

function drag(e) {
  if (!dragging.value) {
    document.body.style.pointerEvents = "none";
    document.documentElement.style.cursor = "ew-resize";
  }
  dragging.value = true;

  const deltaX = beforePosition.pageX - e.pageX;
  const deltaY = beforePosition.pageY - e.pageY;
  const value = beforePosition.value - (deltaX + deltaY) * props.step;

  setValue(value);
  emit("input", newValue.value);
}

function savePosition(e) {
  beforePosition.pageX = e.pageX;
  beforePosition.pageY = e.pageY;
  beforePosition.value = newValue.value;
}

function resetPosition() {
  beforePosition.pageX = 0;
  beforePosition.pageY = 0;
  beforePosition.value = 0;
}

function decrease() {
  setValue(newValue.value - props.step);
  emit("change", newValue.value);
}

function increase() {
  setValue(newValue.value + props.step);
  emit("change", newValue.value);
}

function input(e) {
  let currValue = e.target.value;
  const oldValue = displayValue.value;

  displayValue.value = currValue;

  if (currValue) {
    currValue = parseFloat(currValue);
  }
  if (Number.isNaN(currValue)) {
    displayValue.value = oldValue;
  }
}

function change(e) {
  let currValue = e.target.value;

  if (currValue === "") {
    displayValue.value = currValue = "0";
  }
  const value = parser(currValue);
  setValue(value);
  emit("change", newValue.value);
  emit("update:modelValue", newValue.value);
}

function parser(value) {
  value = parseFloat(value);

  switch (props.formatter) {
    case "percent":
      value = value / 100;
      break;
    case "angle":
      value = value * (Math.PI / 180);
      break;
    case "second":
    case "sec":
      value = value * 30;
      break;
  }
  return value;
}

function formatter(value) {
  if (props.rounded) {
    value = Math.round(value);
  }
  switch (props.formatter) {
    case "percent":
      value = Math.round(value * 100);
      break;
    case "angle":
      value = Math.round(value / (Math.PI / 180));
      break;
    case "second":
    case "sec":
      value = (value / 30).toFixed(1);
      break;
  }
  return value.toString();
}

function setValue(value) {
  if (typeof props.max === "number") {
    value = Math.min(props.max, value);
  }
  if (typeof props.min === "number") {
    value = Math.max(props.min, value);
  }
  newValue.value = value;
  displayValue.value = formatter(value);
}

function focus() {
  oldValue.value = newValue.value;
  isFocus.value = true;
  element.value.select();
}

function blur() {
  isFocus.value = false;
  emit("blur");
}
</script>
<style lang="scss" scoped>
.el-input {
  &__wrapper {
    justify-content: flex-start;
  }
  &__prefix {
    min-width: 14px;
    cursor: ew-resize;
  }
  &__inner {
    max-width: 5ch;
    overflow: hidden;
    flex: 0 0;
    font-variant: tabular-nums;

    &:focus {
      outline: none;
      box-shadow: none;
    }
    &::selection {
      background-color: #dbcfff;
    }
  }

  &__suffix {
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    color: #060606;

    &-inner > :first-child {
      margin-left: 0;
    }
  }

  &--center {
    justify-content: center;
  }

  &--right {
    justify-content: space-between;
  }
}
</style>
